import React, { ReactElement, useCallback, useEffect, useMemo } from "react";
// design-system: rn-primitive waiver — dynamic style / behavior-coupled
import {
  LayoutChangeEvent,
  LayoutRectangle,
  Platform,
  type StyleProp,
  // eslint-disable-next-line no-restricted-imports -- RFC-#6 genuine-runtime: onLayout measurement (LayoutRectangle) feeding a Reanimated useAnimatedStyle translateX interpolation; renders View onLayout…
  View,
  type ViewStyle,
} from "react-native";
import Animated, {
  cancelAnimation,
  FadeInUp,
  FadeOut,
  interpolate,
  ReduceMotion,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from "react-native-reanimated";

import SkeletonPlaceholderView from "./SkeletonPlaceholderView";

import { useTheme } from "@/providers/ThemeProvider";

/**
 * Props for the SkeletonPlaceholderContainer component
 */
interface Props {
  children: ReactElement;
}

/**
 * Container component that wraps content with animated skeleton placeholder effect
 * @param root0 - Component props
 * @param root0.children - React element to be masked with skeleton animation
 */
const SkeletonPlaceholderContainer = ({ children }: Props) => {
  const [layout, setLayout] = React.useState<LayoutRectangle>();
  const sharedAnimated = useSharedValue(0);
  const { resolvedTheme } = useTheme();
  const isDark = useMemo(() => resolvedTheme === "dark", [resolvedTheme]);

  const animStyle = useAnimatedStyle(() => {
    const x = interpolate(
      sharedAnimated.value,
      [0, 1],
      [layout ? -layout.width : 0, layout ? layout.width : 0]
    );
    return {
      transform: [{ translateX: x }],
    };
  });

  const handleLayout = useCallback(
    (e: LayoutChangeEvent) => setLayout(e.nativeEvent.layout),
    []
  );

  useEffect(() => {
    sharedAnimated.set(
      withRepeat(
        withTiming(1, { duration: 2000, reduceMotion: ReduceMotion.System }),
        Infinity
      )
    );
    return () => cancelAnimation(sharedAnimated);
  }, [sharedAnimated]);

  if (!layout) {
    return <View onLayout={handleLayout}>{children}</View>;
  }

  return (
    <SkeletonPlaceholderView
      layoutWidth={layout.width}
      layoutHeight={layout.height}
      animStyle={animStyle as unknown as StyleProp<ViewStyle>}
      isDark={isDark}
    >
      {/* Use layout animations only on web to avoid Reanimated 4.x issues on native */}
      {Platform.OS === "web" ? (
        <Animated.View
          entering={FadeInUp.duration(500)}
          exiting={FadeOut.duration(300)}
        >
          {children}
        </Animated.View>
      ) : (
        <View>{children}</View>
      )}
    </SkeletonPlaceholderView>
  );
};

export default SkeletonPlaceholderContainer;
